import { useSortable } from '@dnd-kit/sortable';
import { Form } from 'antd';
import { Colors, IcArrange, IcClose, Row, Switch, Text } from 'basic-ui-lib';
import BaseTimePicker from 'basic-ui-lib/components/Input/InputTime';
import dayjs from 'dayjs';
import { IContentItem } from 'modules/playlist/interfaces/api';
import styled from 'styled-components';
import { ContentFormValues } from '.';
import thumbnailPlaceholder from 'assets/playlist-default.png';

interface PlaylistContentItemMobileProps {
  item: IContentItem;
  id: number;
  onRemove: () => void;
  ids: number;
}

const PlaylistContentItemMobile = ({ item, id, onRemove, ids }: PlaylistContentItemMobileProps) => {
  const form = Form.useFormInstance<ContentFormValues>();

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const handleChangeValue = (value: dayjs.Dayjs | null) => {
    const currentContentItems = form.getFieldValue('contentItems') || [];
    const newContentItems = [...currentContentItems];
    if (value) {
      const durationInSeconds = value.hour() * 3600 + value.minute() * 60 + value.second();
      newContentItems[ids] = {
        ...newContentItems[ids],
        duration: durationInSeconds,
      };
      form.setFieldsValue({ contentItems: newContentItems });
    }
  };

  return (
    <Container
      ref={setNodeRef}
      style={{
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
    >
      <Row alignItems='center' flex={1}>
        <button {...attributes} {...listeners} onClick={(e) => e.stopPropagation()} type='button'>
          <IcArrange width={12} height={12} fill={Colors.neutral.bg[7]} />
        </button>
        <MediaContent>
          <img src={item.thumbnail || thumbnailPlaceholder} alt={item.name} className='thumbnail'/>
        </MediaContent>
        <Text className='name-text' body_1 medium ellipsis lineClamp={2} left>
          {item.name}
        </Text>
      </Row>
      <BaseTimePicker
        needConfirm={false}
        showNow={false}
        suffixIcon={null}
        allowClear={false}
        className='styled-time-picker'
        value={typeof item.duration === 'number' ? dayjs().startOf('day').add(item.duration, 'second') : undefined}
        onChange={(value) => handleChangeValue(value)}
      />
      <Row alignItems='center' gap={8} justifyContent='end'>
        <Form.Item
          name={[ids, 'autoReplay']}
          noStyle
        >
          <Switch />
        </Form.Item>

        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          type='button'
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <IcClose width={20} height={20} fill={Colors.neutral.bg[7]} />
        </button>
      </Row>
    </Container>
  );
};

export default PlaylistContentItemMobile;

const Container = styled.div`
  display: flex;
  padding: 12px 8px;
  align-items: center;
  border: 1px solid ${Colors.neutral.border[1]};
  border-radius: 8px;
  background-color: ${Colors.neutral.bg[1]};
  button {
    text-align: end;
  }

  .name-text {
    flex: 1;
  }

  .styled-time-picker {
    width: fit-content !important;
    padding: 8px 4px;
  }
  .styled-time-picker .ant-picker-input > input {
    width: 60px;
    text-align: center;
    font-size: 14px;
  }

  gap: 8px;
`;

const MediaContent = styled.div`
  width: 56px;
  min-width: 56px;
  height: 32px;
  position: relative;

  .thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 4px;
  }
`;
